import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Package, Filter, ChevronDown, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/useAuth';
import { useBrowseCategories } from '@/components/estimates/line-items/hooks/useBrowseCategories';
import { useLineItemDelete } from './line-items/hooks/useLineItemDelete';
import LineItemTableRow from './line-items/LineItemTableRow';
import LineItemDetailsDialog from './line-items/LineItemDetailsDialog';
import { useInternalLineItemFilterBuilder } from '@/pages/internal-line-items/hooks/useInternalLineItemFilterBuilder';
import { useInternalLineItemData } from '@/pages/internal-line-items/hooks/useInternalLineItemData';
import { FilterBuilder } from '@/components/invoices/FilterBuilder';
import PageLayout from '@/components/layout/PageLayout';
import PageHeader from '@/components/layout/PageHeader';
import DataTableWrapper from '@/components/shared/DataTableWrapper';
import ViewToggle from '@/components/shared/ViewToggle';
import FilterToolbar from '@/components/shared/FilterToolbar';
import { useViewPreference } from '@/hooks/useViewPreference';
import InternalLineItemsCardView from './InternalLineItemsCardView';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';

interface BrowseCat {
  id: string;
  name: string;
  parent_id: string | null;
  depth: number;
  display_order: number;
  company_id?: string | null;
  is_global?: boolean;
}

const BrowseCategoryNode = ({
  category,
  allCategories,
  selectedId,
  onSelect,
  depth,
}: {
  category: BrowseCat;
  allCategories: BrowseCat[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  depth: number;
}) => {
  const [expanded, setExpanded] = useState(false);
  const children = allCategories.filter(c => c.parent_id === category.id);
  const hasChildren = children.length > 0;
  const isSelected = selectedId === category.id;

  return (
    <div>
      <div
        className={`flex items-center gap-1 px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-muted ${
          isSelected ? 'bg-primary/10 text-primary font-medium' : ''
        }`}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        onClick={() => onSelect(isSelected ? null : category.id)}
      >
        {hasChildren ? (
          <button
            type="button"
            className="p-0.5 hover:bg-muted-foreground/10 rounded"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(prev => !prev);
            }}
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <span className="truncate">{category.name}</span>
        {!category.is_global && (
          <span className="ml-1 text-[10px] font-medium px-1 py-0.5 rounded bg-primary/10 text-primary">
            Custom
          </span>
        )}
      </div>
      {hasChildren && expanded && (
        <div>
          {children
            .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name))
            .map(child => (
              <BrowseCategoryNode
                key={child.id}
                category={child}
                allCategories={allCategories}
                selectedId={selectedId}
                onSelect={onSelect}
                depth={depth + 1}
              />
            ))}
        </div>
      )}
    </div>
  );
};

const InternalLineItemsList = () => {
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const { profile } = useAuth() as any;
  const companyId = profile?.company_id ?? null;

  // View preference
  const { viewMode, setViewMode } = useViewPreference({
    entityType: 'internal-line-items',
    defaultView: 'list'
  });
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const effectiveViewMode = windowWidth < 1024 ? 'grid' : viewMode;

  // Use the data hook for search/sort functionality
  const {
    lineItems,
    fetchLineItems,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
    isLoading,
    error,
    page,
    setPage,
    totalCount,
    totalPages,
    pageSize,
    itemTypes,
    setItemTypes,
    setBrowseCategoryId,
  } = useInternalLineItemData();

  // Browse category panel
  const [browsePanelOpen, setBrowsePanelOpen] = useState(false);
  const [selectedBrowseCategory, setSelectedBrowseCategory] = useState<string | null>(null);

  const isMobileBrowse = windowWidth < 1024;

  const handleBrowseCategorySelect = (id: string | null) => {
    setSelectedBrowseCategory(id);
    setBrowseCategoryId(id);
    setPage(1);
    if (isMobileBrowse) {
      setBrowsePanelOpen(false);
    }
  };

  // System item markup override state
  const [systemOverrideMarkup, setSystemOverrideMarkup] = useState<number>(0);

  // Fetch browse categories (global + company) via shared hook
  const { flat: browseCategories } = useBrowseCategories();


  const { handleDelete } = useLineItemDelete(fetchLineItems);

  // Initialize filter builder with internal items only
  const internalItemsForFilter = (lineItems || []).filter(
    item => !item.item_type || item.item_type === 'internal'
  );
  const filterBuilder = useInternalLineItemFilterBuilder(internalItemsForFilter);

  // System items bypass the filter builder entirely
  const systemItems = (lineItems || []).filter(
    (item: any) => item.item_type === 'system'
  );

  const mergedItems = [
    ...(filterBuilder.filteredLineItems ?? []),
    ...systemItems,
  ];

  const finalFilteredItems = mergedItems;


  const handleEdit = (id: string) => {
    navigate(`/cost-center/internal-line-items/edit/${id}`);
  };

  const handleRowClick = (item: any) => {
    setSelectedItem(item);
    if (item.item_type === 'system') {
      setSystemOverrideMarkup(
        item.custom_markup ?? item.markup_percentage ?? 0
      );
    } else {
      setSystemOverrideMarkup(0);
    }
  };

  const getActiveFilterCount = () => {
    return filterBuilder.appliedGroups.reduce((count, group) => count + group.rules.length, 0);
  };

  const typeTabs = [
    { value: null as string | null, label: 'All types' },
    { value: 'internal', label: 'Internal' },
    { value: 'system', label: 'System' },
  ];

  const selectedBrowseCategoryName = selectedBrowseCategory
    ? (browseCategories ?? []).find(c => c.id === selectedBrowseCategory)?.name ?? null
    : null;

  return (
    <PageLayout>
      <PageHeader
        title="Line Items"
        description="Browse internal and system line items"
      />

      <Card>
        <CardHeader className="space-y-4">
          {/* Type pill tabs */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-muted">
              {typeTabs.map(({ value, label }) => {
                const isActive = value === null
                  ? !itemTypes || itemTypes.length === 0
                  : itemTypes?.[0] === value;
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => {
                      setItemTypes(value ? [value] : null);
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-background text-foreground shadow-sm border border-border/50'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {windowWidth >= 1024 && (
              <ViewToggle 
                value={effectiveViewMode} 
                onChange={setViewMode} 
              />
            )}
          </div>

          {/* Unified filter toolbar — Browse lives in the row-2 leading slot */}
          <FilterToolbar
            actions={
              <Button
                onClick={() => navigate('/cost-center/internal-line-items/create')}
                className="h-9"
              >
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Add Line Item</span>
              </Button>
            }
            leadingFilterAction={
              <Button
                size="sm"
                variant="outline"
                onClick={() => setBrowsePanelOpen(prev => !prev)}
                className="h-9 shrink-0 gap-1.5"
                aria-label="Browse categories"
              >
                <Filter className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">Browse</span>
              </Button>
            }
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search line items..."
            sortOptions={[
              { value: 'name', label: 'Name' },
              { value: 'final_price', label: 'Final Price' },
              { value: 'base_cost', label: 'Base Cost' },
              { value: 'updated_at', label: 'Date Updated' },
            ]}
            sortValue={sortBy}
            onSortChange={setSortBy}
            sortDir={sortDir}
            onSortDirChange={setSortDir}
            advancedFilter={
              <FilterBuilder
                draftGroups={filterBuilder.draftGroups}
                appliedGroups={filterBuilder.appliedGroups}
                fields={filterBuilder.filterFields}
                entities={filterBuilder.filterEntities}
                attributesByEntity={filterBuilder.filterAttributesByEntity}
                onAddGroup={filterBuilder.addGroup}
                onUpdateGroup={filterBuilder.updateGroup}
                onRemoveGroup={filterBuilder.removeGroup}
                onAddRuleToGroup={filterBuilder.addRuleToGroup}
                onUpdateRuleInGroup={filterBuilder.updateRuleInGroup}
                onRemoveRuleFromGroup={filterBuilder.removeRuleFromGroup}
                onClearAll={filterBuilder.clearAllRules}
                onApplyFilters={filterBuilder.applyFilters}
                hasActiveFilters={filterBuilder.hasActiveFilters}
                hasUnappliedChanges={filterBuilder.hasUnappliedChanges}
                canApplyFilters={filterBuilder.canApplyFilters}
              />
            }
            hasAdvancedFilters={filterBuilder.hasActiveFilters}
            advancedFilterCount={getActiveFilterCount()}
            onClearAdvancedFilters={filterBuilder.clearAllRules}
          />

          {/* Selected category chip (instant-apply, reads like a filter without behaving like one) */}
          {selectedBrowseCategoryName && (
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="gap-1 pr-1 h-7">
                <span className="truncate max-w-[10rem]">{selectedBrowseCategoryName}</span>
                <button
                  type="button"
                  onClick={() => handleBrowseCategorySelect(null)}
                  className="ml-1 hover:bg-muted rounded-full p-0.5"
                  aria-label="Clear browse category"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            </div>
          )}
        </CardHeader>
        <div className="flex gap-0">
          {(() => {
            const pickerBody = (
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Filter className="h-4 w-4" />
                    Browse
                  </div>
                  {selectedBrowseCategory && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => handleBrowseCategorySelect(null)}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <div
                  className={`px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-muted ${
                    !selectedBrowseCategory ? 'bg-primary/10 text-primary font-medium' : ''
                  }`}
                  onClick={() => handleBrowseCategorySelect(null)}
                >
                  All
                </div>
                {(browseCategories ?? [])
                  .filter(c => !c.parent_id)
                  .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name))
                  .map(root => (
                    <BrowseCategoryNode
                      key={root.id}
                      category={root}
                      allCategories={browseCategories ?? []}
                      selectedId={selectedBrowseCategory}
                      onSelect={handleBrowseCategorySelect}
                      depth={0}
                    />
                  ))}
              </div>
            );

            if (isMobileBrowse) {
              return (
                <Drawer open={browsePanelOpen} onOpenChange={setBrowsePanelOpen}>
                  <DrawerContent className="max-h-[85vh]">
                    <DrawerHeader className="pb-2">
                      <DrawerTitle>Browse Categories</DrawerTitle>
                    </DrawerHeader>
                    <div className="overflow-y-auto">{pickerBody}</div>
                  </DrawerContent>
                </Drawer>
              );
            }

            return browsePanelOpen ? (
              <div className="relative w-56 shrink-0 border-r overflow-y-auto">
                {pickerBody}
              </div>
            ) : null;
          })()}


          <div className="flex-1 min-w-0">
            <CardContent className={effectiveViewMode === 'list' ? 'p-0' : undefined}>
              <DataTableWrapper
                isLoading={isLoading}
                isEmpty={!finalFilteredItems || finalFilteredItems.length === 0}
                emptyState={{
                  title: "No line items found",
                  description: search || filterBuilder.hasActiveFilters || selectedBrowseCategory
                    ? "No items match your search or filters."
                    : (!itemTypes || itemTypes.length === 0)
                    ? "No line items yet. Add internal items or system items will appear automatically."
                    : itemTypes[0] === 'system'
                    ? "No system items found."
                    : "Get started by adding your first internal line item.",
                  icon: <Package className="h-12 w-12 text-muted-foreground" />,
                  action: (
                    <Button onClick={() => navigate('/cost-center/internal-line-items/create')} className="mt-4">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Line Item
                    </Button>
                  )
                }}
              >
                {effectiveViewMode === 'list' ? (
                  <Table className="table-fixed w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12 hidden sm:table-cell overflow-hidden" aria-label="Image" />
                        <TableHead className="max-w-0 w-full">Title / Description</TableHead>
                        <TableHead className="hidden sm:table-cell w-32 overflow-hidden">Unit</TableHead>
                        <TableHead className="w-36 overflow-hidden">Final Cost</TableHead>
                        <TableHead className="hidden lg:table-cell w-44 overflow-hidden">Classification</TableHead>
                        <TableHead className="hidden lg:table-cell w-24 overflow-hidden">Type</TableHead>
                        <TableHead className="text-right w-20 overflow-hidden">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {finalFilteredItems?.map((item) => (
                      <LineItemTableRow
                        key={item.id}
                        item={item}
                        onRowClick={handleRowClick}
                        onDelete={handleDelete}
                        searchQuery={search}
                      />
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <InternalLineItemsCardView
                    items={finalFilteredItems || []}
                    onItemClick={handleRowClick}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                )}
              </DataTableWrapper>

              {totalPages > 1 && (
                <div className="flex items-center justify-between gap-2 px-4 py-3 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </Button>
                  <div className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </div>
        </div>
      </Card>

      <LineItemDetailsDialog
        selectedItem={selectedItem}
        onClose={() => {
          setSelectedItem(null);
          setSystemOverrideMarkup(0);
        }}
        onEdit={handleEdit}
        systemOverrideMarkup={systemOverrideMarkup}
        setSystemOverrideMarkup={setSystemOverrideMarkup}
        onToggleSystemOverride={async () => {
          if (!selectedItem?.id) return;
          if (selectedItem.override_markup) {
            await (supabase as any)
              .from('company_system_item_markup_overrides')
              .delete()
              .eq('system_line_item_id', selectedItem.id)
              .eq('company_id', companyId);
            fetchLineItems();
            setSelectedItem((prev: any) => prev
              ? { ...prev, override_markup: false, custom_markup: undefined }
              : null
            );
          } else {
            setSystemOverrideMarkup(selectedItem.markup_percentage ?? 0);
            setSelectedItem((prev: any) => prev
              ? { ...prev, override_markup: true }
              : null
            );
          }
        }}
        onSaveSystemOverride={async () => {
          if (!selectedItem?.id) return;
          await (supabase as any)
            .from('company_system_item_markup_overrides')
            .upsert({
              company_id: companyId,
              system_line_item_id: selectedItem.id,
              markup_percentage: systemOverrideMarkup,
            }, {
              onConflict: 'company_id,system_line_item_id'
            });
          fetchLineItems();
          setSelectedItem((prev: any) => prev
            ? {
                ...prev,
                override_markup: true,
                custom_markup: systemOverrideMarkup,
                markup_source: 'item_override',
                markup_percentage: systemOverrideMarkup
              }
            : null
          );
        }}
        onClearSystemOverride={async () => {
          if (!selectedItem?.id) return;
          await (supabase as any)
            .from('company_system_item_markup_overrides')
            .delete()
            .eq('system_line_item_id', selectedItem.id)
            .eq('company_id', companyId);
          fetchLineItems();
          setSelectedItem((prev: any) => {
            if (!prev) return null;
            const fallbackMarkup = prev.default_markup_percent ?? 0;
            return {
              ...prev,
              override_markup: false,
              custom_markup: undefined,
              markup_percentage: fallbackMarkup,
              markup_source: prev.default_markup_percent != null
                ? 'system_default'
                : 'company_category_tier',
            };
          });
        }}
      />
    </PageLayout>
  );
};

export default InternalLineItemsList;
